import {
  DOCUMENT_STATUS,
  SELF_SERVICE_ORIGIN,
  SELF_SERVICE_STATUS,
  SELF_SERVICE_TYPE,
} from 'constants/SelfServiceConstants';
import { COLUMN, SELF_SERVICE_KANBAN_STATUS_TO_COLUMN } from 'modules/interventions/constants/kanban';
import {
  canSendSelfService,
  canSendSelfServiceType,
  disabledSelfServiceMessagesRules,
} from 'services/InterventionUtils';

import { isSelfServiceStatusBelow } from './utils/selfServiceStatus';
import kanbanIcons from '../../components/interventions/Kanban/components/Card/components/Footer/components/kanban.icons';

/**
 * @param {import('./types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {import('modules/selfServices/types').SelfServiceTypeEnum}
 */
export const getSelfServiceWorkflowType = kanbanStatus =>
  ([COLUMN.DEFAULT, COLUMN.HOME_CHECK_IN_COMPLETED, COLUMN.CHECK_IN_COMPLETED].includes(kanbanStatus)
    ? SELF_SERVICE_TYPE.CHECK_IN
    : SELF_SERVICE_TYPE.CHECK_OUT);

/**
 * @param {Pick<import('./types/projections/kanban-intervention').KanbanIntervention, 'selfServices'>} intervention
 * @param {import('modules/selfServices/types').SelfServiceTypeEnum} typeToFind
 * @returns {boolean}
 */
export const isSelfServiceSentOrReplied = ({ selfServices = [] }, typeToFind) => {
  const selfService = selfServices.find(({ type }) => type === typeToFind);

  return (
    Boolean(selfService) &&
    Object.values(selfService.originContext ?? {}).some(({ status }) => status !== SELF_SERVICE_STATUS.TO_BE_SENT)
  );
};

/**
 * @param {Pick<import('./types/projections/kanban-intervention').KanbanIntervention, 'selfServices'>} intervention
 * @param {import('modules/selfServices/types').SelfServiceTypeEnum} typeToFind
 * @returns {boolean}
 */
export const isSelfServiceNotSent = ({ selfServices = [] }, typeToFind) => {
  const selfService = selfServices.find(({ type }) => type === typeToFind);
  return (
    !selfService ||
    Object.values(selfService?.originContext ?? {}).every(({ status }) => status === SELF_SERVICE_STATUS.TO_BE_SENT)
  );
};

/**
 * @param {Pick<import('./types/projections/kanban-intervention').KanbanIntervention, 'startDate' | 'endDate'>} intervention
 * @returns {number}
 */
export const computeProgression = ({ startDate, endDate }) => {
  const start = new Date() - new Date(startDate);
  const range = new Date(endDate ?? startDate) - new Date(startDate);
  if (range === 0) {
    return start < 0 ? 0 : 100;
  }
  return Math.min(100, Math.max(0, Math.round((start * 100) / range)));
};

/**
 * @param {Pick<import('./types/projections/kanban-intervention').KanbanIntervention, 'customerInfo' | 'vehicleInfo' | 'registration'>} intervention
 * @returns {boolean}
 */
export const isDisabledCard = ({ customerInfo, vehicleInfo: tmpVehicleInfo, registration }) => {
  const vehicleInfo = tmpVehicleInfo || { registration };
  return !canSendSelfService({ customerInfo, vehicleInfo });
};

/**
 * @param {import('./types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {boolean}
 */
export const canMoveToInHouse = kanbanStatus =>
  kanbanStatus !== COLUMN.CHECK_OUT_COMPLETED && kanbanStatus !== COLUMN.IN_HOUSE;

/**
 * @param {import('./types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {boolean}
 */
export const canMoveToCheckoutReady = kanbanStatus =>
  [COLUMN.DEFAULT, COLUMN.HOME_CHECK_IN_COMPLETED, COLUMN.CHECK_IN_COMPLETED, COLUMN.IN_HOUSE].includes(kanbanStatus);

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @param {import('./types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {boolean}
 */
export const canShowSendIcon = (intervention, kanbanStatus) => {
  const isDisabled = isDisabledCard(intervention);
  const selfServiceType = getSelfServiceWorkflowType(kanbanStatus);
  const isSent = isSelfServiceSentOrReplied(intervention, selfServiceType);

  return !isDisabled && isSent && (kanbanStatus === COLUMN.DEFAULT || kanbanStatus === COLUMN.CHECK_OUT_READY);
};

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @param {import('./types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @param {import('date-fns').Locale} locale
 * @returns {boolean}
 */
export const canShowSendActionButton = (intervention, kanbanStatus, locale) => {
  const { vehicleInfo: tmpVehicleInfo } = intervention;
  const vehicleInfo = tmpVehicleInfo || { registration: intervention.registration };

  const isDisabled = isDisabledCard(intervention);
  const selfServiceType = getSelfServiceWorkflowType(kanbanStatus);
  const isNotSent = isSelfServiceNotSent(intervention, selfServiceType);

  const isSendable = canSendSelfServiceType(
    selfServiceType,
    { ...intervention, vehicleInfo },
    { hasFinalInvoice: Boolean(intervention.finalInvoiceStatus), locale }
  );

  return (
    !isDisabled &&
    isSendable &&
    isNotSent &&
    (kanbanStatus === COLUMN.DEFAULT || kanbanStatus === COLUMN.CHECK_OUT_READY)
  );
};

/**
 * @param {Pick<import('./types/projections/kanban-intervention').KanbanIntervention, 'selfServices' | 'finalInvoiceStatus'>} intervention
 * @param {import('./types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {boolean}
 */
export const canSendFromOverlay = (intervention, kanbanStatus) => {
  const { selfServices = [] } = intervention;

  const selfServiceType = getSelfServiceWorkflowType(kanbanStatus);
  const selfService = selfServices.find(({ type }) => type === selfServiceType);

  const isSendable =
    disabledSelfServiceMessagesRules[selfServiceType]?.(intervention, Boolean(intervention.finalInvoiceStatus)) ===
    undefined;

  return (
    [COLUMN.DEFAULT, COLUMN.CHECK_OUT_READY].includes(kanbanStatus) &&
    isSendable &&
    !isSelfServiceStatusBelow(
      selfService?.originContext?.HOME?.status ?? SELF_SERVICE_STATUS.TO_BE_SENT,
      SELF_SERVICE_STATUS.SENT
    )
  );
};

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @param {import('./types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {Array<import('../../components/interventions/Kanban/components/Card/components/Footer/components/kanban.icons').KanbanIcon>}
 */
export const getIconsList = (intervention, kanbanStatus) =>
  kanbanIcons.reduce((acc, icon) => (icon.isVisible(intervention, kanbanStatus) ? [icon, ...acc] : acc), []);

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {number}
 */
export const getExtraAcceptedCount = ({ acceptedExtraCount = 0 }) => acceptedExtraCount;

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {number}
 */
export const getProactiveAcceptedCount = ({ acceptedProactiveCount = 0 }) => acceptedProactiveCount;

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @param {import("modules/selfServices/types").selfServiceType} intervention
 * @returns {boolean}
 */
export const isFinalized = ({ selfServices = [] }, selfServiceType) => {
  const selfService = selfServices.find(({ type }) => type === selfServiceType);
  return selfService?.finalized;
};

export const hasDocumentsToValidate = ({ selfServices = [] }) => {
  const checkIn = selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_IN);

  // Dealers can only validate documents if *only* HOME check-in is answered
  const canValidateDocuments =
    Boolean(checkIn?.originContext) &&
    Object.entries(checkIn.originContext).reduce((canValidate, [origin, { status }]) => {
      if (origin === SELF_SERVICE_ORIGIN.HOME) {
        return canValidate && status === SELF_SERVICE_STATUS.ANSWERED;
      }

      return canValidate && status !== SELF_SERVICE_STATUS.ANSWERED;
    }, true);

  const hasDocumentsToBeValidated =
    checkIn?.mobility?.documents?.some(({ status }) => status === DOCUMENT_STATUS.TO_VALIDATE) ?? false;

  return canValidateDocuments && hasDocumentsToBeValidated;
};

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {boolean}
 */
export const hasMessage = ({ commentPresent }) => Boolean(commentPresent);

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {boolean}
 */
export const shouldDisplayLegend = intervention => {
  const adviceCount = getExtraAcceptedCount(intervention) + getProactiveAcceptedCount(intervention);
  return hasMessage(intervention) || hasDocumentsToValidate(intervention) || adviceCount > 0;
};

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {import('modules/selfServices/types').SelfService}
 */
export const getCheckout = ({ selfServices = [] }) => selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_OUT);

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {boolean}
 */
export const hasCheckout = ({ selfServices } = {}) => Boolean(getCheckout({ selfServices }));

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {boolean}
 */
export const hasManuallyCompletedCheckout = ({ selfServices } = {}) => (
  getCheckout({ selfServices })?.originContext?.[SELF_SERVICE_ORIGIN.MANUAL]?.status === SELF_SERVICE_STATUS.ANSWERED
);

/**
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {boolean}
 */
export const hasHomeCheckoutAnswered = ({ selfServices } = {}) => (
  getCheckout({ selfServices })?.originContext?.[SELF_SERVICE_ORIGIN.HOME]?.status === SELF_SERVICE_STATUS.ANSWERED
);

/**
 * @description A Fully completed check-out is:
 *
 * - When the last check-out sent had no kiosk option → fully completed check-out is when the home check-out is done OR the on-site check-out is done.
 * - When the last check-out sent was a kiosk check-out → fully completed check-out is when the kiosk check-out is done OR the on-site check-out is done.
 *
 * @see https://fleetback.atlassian.net/browse/BMWVID-24239
 *
 * @param {import('./types/projections/kanban-intervention').KanbanIntervention} intervention
 * @returns {boolean}
 */
export const isCheckoutFullyCompleted = ({ selfServices } = {}) => {
  const selfService = getCheckout({ selfServices });

  if (selfService) {
    const kioskCheckout = selfService.originContext?.[SELF_SERVICE_ORIGIN.KIOSK];
    if (kioskCheckout) {
      return kioskCheckout.status === SELF_SERVICE_STATUS.ANSWERED ||
        selfService.originContext?.[SELF_SERVICE_ORIGIN.ON_SITE]?.status === SELF_SERVICE_STATUS.ANSWERED;
    }

    return Object.entries(selfService.originContext ?? {}).some(([origin, checkout]) => (
      origin !== SELF_SERVICE_ORIGIN.MANUAL &&
      checkout.status === SELF_SERVICE_STATUS.ANSWERED
    ));
  }

  return false;
};

/**
 * @description
 * Can manually complete checkout when:
 * - We are in the CHECK_OUT_COMPLETED column
 * - The checkout is NOT already manually completed
 * - The checkout is NOT already completed (@see https://fleetback.atlassian.net/browse/BMWVID-24239 for more information)
 *
 * @param {import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention} intervention
 * @param {import('modules/interventions/types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {boolean} Can the checkout be manually completed
 */
export const canManuallyCompleteCheckout = ({ id, selfServices = [] }, kanbanStatus) => (
  kanbanStatus === COLUMN.CHECK_OUT_COMPLETED && !hasManuallyCompletedCheckout({ selfServices }) && !isCheckoutFullyCompleted({ id, selfServices })
);

/**
 * @description
 * Can manually uncomplete checkout when:
 * - We are in the CHECK_OUT_COMPLETED column
 * - We *already* were in this column before (ie. the customer completed the checkout)
 * - The checkout is currently manually completed
 *
 * @param {import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention} intervention
 * @param {import('modules/interventions/types/interfaces/kanban').KanbanEnum} selfServiceKanbanStatus
 * @param {import('modules/interventions/types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {boolean} Can the checkout be manually uncompleted
 */
export const canRemoveManuallyCompletedCheckout = ({ id, selfServices = [] }, kanbanStatus) => (
  kanbanStatus === COLUMN.CHECK_OUT_COMPLETED &&
  hasManuallyCompletedCheckout({ selfServices }) &&
  !isCheckoutFullyCompleted({ id, selfServices }) &&
  hasHomeCheckoutAnswered({ selfServices })
);

/**
 * @description
 * Can drag to in house or checkout ready
 *
 * @param {import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention} intervention
 * @param {import('modules/interventions/types/interfaces/kanban').KanbanEnum} selfServiceKanbanStatus
 * @param {import('modules/interventions/types/interfaces/kanban').KanbanEnum} kanbanStatus
 * @returns {boolean} Can the checkout be manually uncompleted
 */
export const canDragFromCompletedCheckout = ({ selfServices = [], selfServiceKanbanStatus }, kanbanStatus) => {
  const previousKanbanStatus = SELF_SERVICE_KANBAN_STATUS_TO_COLUMN[selfServiceKanbanStatus] ?? selfServiceKanbanStatus;

  return (
    kanbanStatus === COLUMN.CHECK_OUT_COMPLETED &&
    (!previousKanbanStatus || previousKanbanStatus === COLUMN.IN_HOUSE || previousKanbanStatus === COLUMN.CHECK_OUT_READY) &&
    hasManuallyCompletedCheckout({ selfServices }) &&
    !isCheckoutFullyCompleted({ selfServices }) &&
    !hasHomeCheckoutAnswered({ selfServices })
  );
};
